import { isListPageSize, listPageSizeStorageKey } from '@/lib/list-page-size';
import { browserStorage } from '@/lib/ui-prefs';
import { lastViewStorageKey } from '@/lib/url-state/last-view';
import { readUrlState, rememberedQuery, type UrlSchema } from '@/lib/url-state/url-schema';
import { PRODUCTS_URL, PRODUCTS_VIEW_ID } from '@/pages/products-url';
import { MEDIA_URL, MEDIA_VIEW_ID } from '@/pages/media-url';
import {
  PRODUCTS_BULK_EDIT_URL,
  PRODUCTS_BULK_EDIT_VIEW_ID,
} from '@/pages/products-bulk-edit/bulk-edit-url';
import {
  COLLECTIONS_BULK_EDIT_URL,
  COLLECTIONS_BULK_EDIT_VIEW_ID,
} from '@/pages/collections-bulk-edit/collections-bulk-edit-url';
import { TASKS_URL, TASKS_VIEW_ID } from '@/pages/tasks/board-url';
import { MARKETING_URL, MARKETING_VIEW_ID } from '@/pages/marketing/marketing-filters-url';

/**
 * Until T-196, /products, /media and the two bulk-edit pages kept their whole view in one blob
 * per team (`products_list_view_<teamId>` and the like). Their filters and sort now live in the
 * URL and the page's last view; the page size moved to useTablePaging's own key
 * (`list_page_size_<listId>_<teamId>`).
 */
type Blob = Record<string, unknown>;

/** A blob's remembered filters and sort as the URL its page wrote before T-196 (the old keys). */
function oldQuery(blob: Blob, lists: string[], ranges: Record<string, [string, string]>, flags: string[], keys: string[]) {
  const params = new URLSearchParams();
  for (const key of lists) {
    const value = blob[key];
    if (Array.isArray(value)) params.set(key, value.filter((v) => typeof v === 'string').join(','));
  }
  for (const [key, [minKey, maxKey]] of Object.entries(ranges)) {
    const range = blob[key] as { min?: unknown; max?: unknown } | undefined;
    if (typeof range?.min === 'number') params.set(minKey, String(range.min));
    if (typeof range?.max === 'number') params.set(maxKey, String(range.max));
  }
  for (const key of flags) if (blob[key] === true) params.set(key, 'true');
  for (const key of keys) {
    const value = blob[key];
    if (typeof value === 'string' || typeof value === 'boolean') params.set(key, String(value));
  }
  // An empty list meant "no filter", which the old URL said by leaving the key out.
  for (const [key, value] of [...params]) if (value === '' && key !== 'productStatuses') params.delete(key);
  return params;
}

const RETIRED_LIST_VIEWS: {
  prefix: string;
  sizeField: string;
  listId: string;
  viewId: string;
  schema: UrlSchema;
  toOldQuery: (blob: Blob) => URLSearchParams;
}[] = [
  {
    prefix: 'products_list_view_',
    sizeField: 'limit',
    listId: 'products',
    viewId: PRODUCTS_VIEW_ID,
    schema: PRODUCTS_URL,
    toOldQuery: (blob) =>
      oldQuery(
        blob,
        ['vendors', 'suppliers', 'productTypes', 'status', 'shopifyStatuses', 'xeroStatus'],
        { stock: ['minStock', 'maxStock'] },
        [],
        ['groupBy', 'sortField', 'sortDirection']
      ),
  },
  {
    prefix: 'media_list_view_',
    sizeField: 'limit',
    listId: 'media',
    viewId: MEDIA_VIEW_ID,
    schema: MEDIA_URL,
    // `productStatuses` is kept even when empty: none chosen meant every status.
    toOldQuery: (blob) =>
      oldQuery(
        blob,
        ['vendors', 'contentTypes', 'fileTypes', 'productStatuses'],
        { fileSize: ['minFileSizeKb', 'maxFileSizeKb'], dimensions: ['minShortestSide', 'maxShortestSide'] },
        ['nonSquareOnly'],
        ['sortField', 'sortDirection']
      ),
  },
  {
    prefix: 'products_bulk_edit_view_',
    sizeField: 'limit',
    listId: 'products-bulk-edit',
    viewId: PRODUCTS_BULK_EDIT_VIEW_ID,
    schema: PRODUCTS_BULK_EDIT_URL,
    toOldQuery: (blob) =>
      oldQuery(
        blob,
        ['vendors', 'productTypes', 'status', 'shopifyStatuses', 'xeroStatus'],
        { stock: ['minStock', 'maxStock'] },
        [],
        ['seoTitle', 'seoDescription', 'teaser', 'sortField', 'sortDirection']
      ),
  },
  {
    prefix: 'collections_bulk_edit_view_',
    sizeField: 'pageSize',
    listId: 'collections-bulk-edit',
    viewId: COLLECTIONS_BULK_EDIT_VIEW_ID,
    schema: COLLECTIONS_BULK_EDIT_URL,
    toOldQuery: (blob) => {
      const params = oldQuery(
        blob,
        [],
        {},
        [],
        ['published', 'seoTitle', 'seoDescription', 'description', 'sortBy', 'sortDir']
      );
      if (Array.isArray(blob.types) && blob.types.length > 0) params.set('type', blob.types.join(','));
      return params;
    },
  },
];

type KeyStore = NonNullable<ReturnType<typeof browserStorage>>;

/** Until T-229 the Board kept its last filters (search too) as a query string under its own key. */
const TASKS_LAST_VIEW_PREFIX = 'jflo.tasks.lastView.';

/** Until T-229 the marketing range was kept per browser, by zustand's persist, under this key. */
const MARKETING_STORE_KEY = 'jflo-marketing-filters-v3';
/** The team a new tab opens on (`UI_PREFS.authLastTeam`). */
const LAST_TEAM_KEY = 'jflo-last-team-id';

/**
 * The browser's old marketing range and granularity become the marketing pages' last view for the
 * team this browser last used (the old key had no team). Kept until a team is known.
 */
function moveMarketingFilters(store: KeyStore): void {
  const raw = store.getItem(MARKETING_STORE_KEY);
  if (raw === null) return;
  const teamId = store.getItem(LAST_TEAM_KEY);
  if (!teamId) return;
  const state = parseBlob(raw)?.state as Blob | undefined;
  const params = new URLSearchParams();
  for (const [from, to] of [['period', 'period'], ['startDate', 'from'], ['endDate', 'to'], ['granularity', 'granularity']]) {
    const value = state?.[from];
    if (typeof value === 'string') params.set(to, value);
  }
  const query = rememberedQuery(MARKETING_URL, readUrlState(MARKETING_URL, params));
  const lastViewKey = lastViewStorageKey(MARKETING_VIEW_ID, teamId);
  if (query && lastViewKey && store.getItem(lastViewKey) === null) store.setItem(lastViewKey, query);
  store.removeItem(MARKETING_STORE_KEY);
}

/** The Board's old last filters become its last view, without the search (decision 24 a). */
function moveTasksLastView(store: KeyStore, key: string): void {
  const teamId = key.slice(TASKS_LAST_VIEW_PREFIX.length);
  const raw = store.getItem(key);
  const query = teamId && raw ? rememberedQuery(TASKS_URL, readUrlState(TASKS_URL, new URLSearchParams(raw))) : '';
  const lastViewKey = teamId ? lastViewStorageKey(TASKS_VIEW_ID, teamId) : '';
  if (query && lastViewKey && store.getItem(lastViewKey) === null) store.setItem(lastViewKey, query);
  store.removeItem(key);
}

function parseBlob(raw: string | null): Blob | null {
  try {
    const blob = JSON.parse(raw ?? 'null') as unknown;
    return blob && typeof blob === 'object' && !Array.isArray(blob) ? (blob as Blob) : null;
  } catch {
    return null;
  }
}

/**
 * Run once at load: give each team's chosen page size to the new key, and its remembered filters
 * and sort to the page's last view (each unless the new key has one already), then delete the old
 * blob, so nothing of the team's is left behind in it. Cheap, and safe to run again; a tab still
 * on old code may write a blob, which the next load moves too.
 */
export function migrateRetiredListViews(store: KeyStore | null = browserStorage('local')): void {
  if (!store) return;
  try {
    moveMarketingFilters(store);
    const keys: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key) keys.push(key);
    }
    for (const key of keys) {
      if (key.startsWith(TASKS_LAST_VIEW_PREFIX)) {
        moveTasksLastView(store, key);
        continue;
      }
      const view = RETIRED_LIST_VIEWS.find((v) => key.startsWith(v.prefix));
      const teamId = view ? key.slice(view.prefix.length) : '';
      if (!view || !teamId) continue;
      const blob = parseBlob(store.getItem(key));
      const size = blob?.[view.sizeField];
      const sizeKey = listPageSizeStorageKey(view.listId, teamId);
      if (typeof size === 'number' && isListPageSize(size) && sizeKey && store.getItem(sizeKey) === null) {
        store.setItem(sizeKey, String(size));
      }
      // Read through the page's schema, so the old keys become the new ones and anything the page
      // no longer offers falls back to its default.
      const lastViewKey = lastViewStorageKey(view.viewId, teamId);
      const query = blob
        ? rememberedQuery(view.schema, readUrlState(view.schema, view.toOldQuery(blob)))
        : '';
      if (query && store.getItem(lastViewKey) === null) store.setItem(lastViewKey, query);
      store.removeItem(key);
    }
  } catch {
    // Blocked or full storage: the blobs stay until a later load can move them.
  }
}
